import type { StatementEsignSummary } from './esign.types'
// ============================================================
// settlementStatement.types.ts — B4: settlement / completion statements
// (backend controllers/settlementStatementController + lib/settlementStatement).
// Money is whole pence.
// ============================================================
import type { NetDestination, PreviewResult } from './settlement.types'

export type StatementStatus = 'DRAFT' | 'ISSUED' | 'APPROVED' | 'SUPERSEDED'
export type StatementKind = 'INTERIM' | 'FINAL'
export type Capacity = 'ADULT' | 'CHILD' | 'PROTECTED_PARTY'
// A block reason's fix — each one is a real action on the page (batch 2):
// 'prepare' marks it prepared, 'approve' is the approver's step, the field
// targets focus that exact field of the statement, 'litigationFriend' opens
// Client details → Litigation friend.
// 'ateCard': the ATE card in the figures drawer (Settlement redesign).
export type FixTarget = 'ateCard' | 'esignSettings' | 'settlementWording' | 'cfa' | 'clientDetails' | 'courtOrder' | 'statement' | 'settlementInputs' | 'check'
  | 'prepare' | 'approve' | 'litigationFriend' | 'settlementDate' | 'damagesReceivedAt' | 'signatory' | 'netDestination' | 'retentionReason' | 'createCfa'
  // (Audit B2) the case's Billing (an unpaid interim bill); the court steps tracker.
  | 'billing' | 'courtSteps'
export interface BlockReason { code: string; reason: string; fix: { label: string; target: FixTarget } }
export interface StatementIssue { code: string; message: string; field?: string }

export type CourtStage =
  | 'AGREED_SUBJECT_TO_APPROVAL' | 'APPLICATION_ISSUED' | 'LISTED' | 'APPROVED' | 'REFUSED' | 'ADJOURNED'
  | 'ORDER_SEALED' | 'PAID_TO_CFO' | 'DEDUCTIONS_RELEASED'
export interface CourtOrder {
  orderDate: string
  courtName: string
  claimNumber: string
  judgeName: string
  approvedDamagesPence: number | null
  approvedDeductionsPence: number | null
  costsAssessment: 'SUMMARY' | 'DETAILED' | null
}
export interface CourtStageStep { stage: CourtStage; on: string; note: string; byUserId: string; at: string }

export type ClientApprovalMethod = 'SIGNED_FORM' | 'EMAIL_REPLY' | 'IN_PERSON' | 'PHONE_RECORDED' | 'E_SIGNATURE'
export const CLIENT_APPROVAL_METHOD_LABELS: Record<ClientApprovalMethod, string> = {
  SIGNED_FORM: 'Signed form', EMAIL_REPLY: 'Email reply', IN_PERSON: 'In person', PHONE_RECORDED: 'Phone call (recorded)', E_SIGNATURE: 'E-signature',
}
export interface ClientResponse {
  id: string
  response: 'AGREED' | 'DISPUTED'
  disputedPence: number | null
  comment: string
  method: ClientApprovalMethod | null
  date: string | null
  contentHash: string | null
  // (Big-audit batch 2, M1) When it was recorded; its attachment; an e-signed
  // answer exists for this version (it can't be changed — a later instruction instead).
  recordedAt?: string
  fileId?: string | null
  esignLocked?: boolean
}
// (Big-audit batch 2, M1) Every answer + later instruction for a version, newest first.
export type LaterInstructionDecision = 'PENDING' | 'APPROVED' | 'REFUSED'
export interface AnswerHistoryEntry {
  id: string
  kind: 'ANSWER' | 'LATER_INSTRUCTION'
  // The one the money follows now.
  current: boolean
  response: 'AGREED' | 'DISPUTED'
  disputedPence: number | null
  comment: string
  method: ClientApprovalMethod | null
  date: string | null
  recordedByUserId: string | null
  recordedBy: string
  recordedAt: string
  file: { id: string; filename: string } | null
  signingRequestId: string | null
  // (Big-audit batch 4, item 7) The firm's only approver approved their own.
  selfApproved?: boolean
  selfNote?: string
  reason?: string
  decision?: LaterInstructionDecision
  decidedBy?: string | null
  decidedAt?: string | null
  refusalReason?: string
}
export interface Readiness {
  ready: boolean
  label: string
  reason: string | null
  netToClientPence: number
  approvedDeductionsPence: number
  heldPence: number
}
export interface StatementSummary {
  id: string
  version: number
  number: string | null
  status: StatementStatus
  kind: StatementKind
  settlementDate: string | null
  damagesReceivedAt: string | null
  retentionPence: number
  retentionReason: string
  netDestination: NetDestination
  netDestinationDetail: string
  shortfallWaived: boolean
  signatoryUserId: string | null
  signatoryName: string | null
  prepared: { at: string; by: string } | null
  // selfNote: approved by the person who prepared it, two-person checks off.
  checked: { at: string; by: string; selfNote?: string } | null
  issuedAt: string | null
  issuedBy: string | null
  contentHash: string | null
  pdfFileId: string | null
  pdfSaved: boolean
  delivered: { at: string; method: 'EMAIL' | 'POST' | 'HAND'; by: string } | null
  supersedes: { id: string; version: number } | null
  supersededBy: { id: string; version: number } | null
  capacity: Capacity
  // chargesBeforeCreditPence: the most the client can dispute (M6).
  figures: { totalDamagesPence: number; totalDeductionsPence: number; netToClientPence: number; chargesBeforeCreditPence?: number }
  // (M3) An open e-signed dispute on an earlier version of this statement.
  carriedDispute?: { fromVersion: number; disputedPence: number; date: string | null } | null
  courtOrder: CourtOrder | null
  courtStage: CourtStage | null
  courtStageHistory: CourtStageStep[]
  clientResponse: ClientResponse | null
  // (Big-audit batch 2, M1)
  answerHistory?: AnswerHistoryEntry[]
  position: { payableToClientPence: number; transferableDeductionsPence: number; heldPence: number } | null
  readiness: Readiness
  copTaskId: string | null
  // (B4 follow-up) Issued before the court order (child / protected party).
  proposed: boolean
  // The approver's tick on the advisory notes (who, when, which).
  notesReviewed: { at: string; by: string; notes: StatementIssue[] } | null
  // (Audit fix H1) The approval was withdrawn because the figures changed.
  approvalWithdrawn?: { at: string; reason: string } | null
  // (Audit fix batch 1, M1) The preparation was withdrawn: the figures changed after it was prepared.
  preparationWithdrawn?: { at: string; reason: string } | null
  // (Audit fix batch 1, H2) "I've checked the difference" with the final bill — who, when, why.
  billMismatchOverride?: MismatchCheckSummary | null
  // The row's version — a save sends it back (baseUpdatedAt; stale → 409).
  updatedAt?: string
  // (Batch 2) The last "Send back for changes" on this draft (who, when, why).
  sentBack?: { at: string; by: string; reason: string } | null
  // (Batch 2) The final bill sent with this statement (one email / post pack).
  finalBill?: { id: string; number: string } | null
  // (Batch 2) Who the statement is addressed to (the litigation friend for a
  // child / protected party): "Ms Sarah Smith, litigation friend for Lily Smith".
  addressee?: { name: string; greeting: string; litigationFriend: boolean } | null
  // (Settlement redesign) The calculation as issued, from the frozen snapshot.
  frozen?: { result: NonNullable<PreviewResult['result']> & { used?: PreviewResult['used'] }; damages: { pslaPence?: number; pastLossPence?: number; futureLossPence?: number; interestPence?: number } | null } | null
}
// (Audit B2, low b) Who checked a bill difference (and the approver who OK'd it, at issue).
export interface MismatchCheckSummary {
  at: string | null; by: string; reason: string; billNumber: string; parts: MismatchPart[]
  approved?: { at: string | null; by: string; self: boolean; checksOff?: boolean } | null
}
export interface StatementDraft extends StatementSummary {
  blockers: BlockReason[]
  // ADVISORY notes — never block; the approver ticks "I've reviewed these notes".
  advisory: StatementIssue[]
  warnings: StatementIssue[]
  errors: StatementIssue[]
  capacity: Capacity
  figures: { totalDamagesPence: number; totalDeductionsPence: number; netToClientPence: number }
  deductionsProposed: boolean
  // (Audit fix H1) The figures shown — the approve request sends it back.
  approvalFingerprint?: string
  // THE rule for this viewer (lib/twoPerson on the server): the screen follows it.
  approval?: { allowed: boolean; needsColleague: boolean; selfBasis: 'ONLY_APPROVER' | 'CHECKS_OFF' | null; preparedByMe: boolean; askable: string[] }
  // (Follow-ups) What stops "Mark as prepared": every fee-earner item (only
  // the approval-stage steps may remain). Preparing locks the figures.
  prepareBlockers?: BlockReason[]
  // (Audit fix batch 1, H2) The case's latest final bill vs these figures, part
  // by part. BLOCKS Issue until it matches or "I've checked the difference"
  // (a reason) is ticked; overridden = an earlier tick covers this difference.
  billMismatch?: BillMismatch | null
  billMismatchOverridden?: boolean
  // (Audit B2, low b) The check recorded on THIS difference (mine = I recorded it),
  // and why Issue still waits (null = it may issue as far as the bill goes).
  billMismatchCheck?: (MismatchCheckSummary & { mine: boolean }) | null
  billMismatchIssueProblem?: string | null
  // (Big-audit batch 4, item 9) "How these deductions were worked out".
  deductionsBreakdown?: DeductionsBreakdown | null
  // (Settlement redesign) The statement's own calculation, from the server.
  calculation?: PreviewResult
}
export interface DeductionsBreakdown {
  lines: { key: string; label: string; nowPence: number }[]
  notes: string[]
}
// (Audit fix batch 1, H2) The final bill and the statement differ on these
// parts (profit costs / success fee / disbursements; VAT in, credit notes off).
export interface MismatchPart { part: 'profitCosts' | 'successFee' | 'disbursements'; label: string; billPence: number; statementPence: number }
export interface BillMismatch { billId: string; billNumber: string; parts: MismatchPart[]; message: string; key: string }
// The override sent with Issue / the pack send / the post-hand record.
export interface MismatchOverrideBody { checked: true; reason: string }
export const MISMATCH_REASON_MAX = 500
export interface SignatoryChoice { id: string; name: string; jobTitle: string | null; isFeeEarner: boolean; hasSignature: boolean }
export interface CaseStatements {
  statements: StatementSummary[]
  draft: StatementDraft | null
  // (Settlement redesign) No statement yet: the draft this case would have,
  // worked out and saved nowhere. The first real action creates it.
  pendingDraft?: StatementDraft | null
  signatories: SignatoryChoice[]
  defaultSignatoryId: string | null
  canCheck: boolean
  onlyApprover: boolean
  twoPersonChecks?: boolean
  // (Big-audit batch 4, item 7) Nobody at all can approve a later instruction.
  noApprover?: boolean
  // (Big-audit batch 2, M1) Who is looking (a later instruction is decided by someone else).
  viewerUserId?: string
  wording: { ok: boolean; reason: string | null }
  courtStages: { key: CourtStage; label: string }[]
  today: string
  issued?: { number: string; contentHash: string; pdfSaved: boolean }
  // (Batch 2) The case's issued final bills that can go with a statement
  // (newest first) — the send window offers "Also send bill INV-…".
  finalBillOptions?: { id: string; number: string; issuedAt: string; totalPence: number; standingPence?: number; delivered?: { at: string; method: string | null } | null }[]
  // (Batch 2) The case's representatives, for payee-aware labels.
  representativeType?: RepresentativeType | null
  litigationFriendName?: string | null
  // (B4c batch 1b) Each issued version's latest e-signature request (pilot only).
  esign?: Record<string, StatementEsignSummary>
  esignPilot?: boolean
  // (E-sign audit fixes A) Issuing / correcting cancels the old version's
  // open signing links — how many couldn't be cancelled, and what to do.
  esignRevokeFailed?: number
  esignRevokeWarning?: string
}
export interface DraftFields {
  settlementDate?: string
  damagesReceivedAt?: string | null
  kind?: StatementKind
  retentionPence?: number
  retentionReason?: string
  netDestination?: NetDestination
  netDestinationDetail?: string
  shortfallWaived?: boolean
  signatoryUserId?: string | null
  proposed?: boolean
  baseUpdatedAt?: string
}
export interface ClientApprovalBody {
  statementVersion: number
  contentHash: string | null
  method: ClientApprovalMethod
  date: string
  response: 'AGREED' | 'DISPUTED'
  disputedPence?: number | null
  comment?: string
  // (Big-audit batch 2, M1) The evidence (a file on this case), when given.
  fileId?: string | null
}
// (Big-audit batch 2, M1) A later instruction after an e-signed answer: the reason + the attachment are required.
export interface LaterInstructionBody extends ClientApprovalBody { reason: string; fileId: string }
export interface CourtOrderBody extends Omit<CourtOrder, 'costsAssessment'> { costsAssessment: 'SUMMARY' | 'DETAILED' | null; note?: string }

// The statement document (what the PDF prints) — utils/settlementStatementHtml.
export type { StatementHtmlDoc as StatementDocument } from '../utils/settlementStatementHtml'

export interface StatementSendDraft {
  to: string
  clientId: string
  clientHasEmail: boolean
  noEmailAddress: boolean
  clientEmail: string | null
  consent: { at: string; email: string | null; method: string | null } | null
  consentChanged: boolean
  emailAllowed: boolean
  emailBlockedReason: string | null
  senderEmail: string
  subject: string
  body: string
  attachmentName: string
  delivered: { at: string; method: string } | null
  recipient?: SendRecipient
  // (Batch 2) The bill offered to go in the same email (the latest issued
  // final bill on the case), or null.
  finalBill?: {
    id: string; number: string; attachmentName: string; subject?: string; body?: string
    // (Audit fix batch 1) Part by part — blocks the pack unless checked + a reason.
    mismatch?: BillMismatch | null; mismatchOverridden?: boolean
    // (Decision, 31 Oct) The bill already reached the client: "Bill already sent by email on 31/10/2026".
    delivered?: { at: string; method: string | null } | null; alreadySent?: string | null
    // (M2) It can't go in this pack at all (addressed to someone else) / can't go by email.
    refusal?: string | null; emailBlockedReason?: string | null
  } | null
  // (Audit fix batch 1, Low 9) The PDF names a different litigation friend from today's.
  addresseeWarning?: string | null
}
// (Follow-ups) A send's reply: how long each step took (also logged on the server).
export interface StatementSendResult { timings?: Record<string, number>; replayed?: boolean; mismatchOverridden?: boolean; [k: string]: unknown }

export interface RegisterRow {
  id: string
  caseId: string
  matter: string
  matterRef: string
  number: string | null
  version: number
  status: StatementStatus
  kind: StatementKind
  capacity: Capacity
  statementDate: string | null
  settlementDate: string | null
  damagesReceivedAt: string | null
  netToClientPence: number
  totalDeductionsPence: number
  approvalStatus: string
  heldPence: number
  readiness: Readiness
  proposed?: boolean
  netDestination: NetDestination
  supersededByVersion: number | null
  delivered: { at: string; method: string } | null
  contentHash: string | null
}

// Admin → Settlement wording.
export type ExplanationKey = 'SUCCESS_FEE' | 'COUNSEL_SUCCESS_FEE' | 'SHORTFALL' | 'ATE_PREMIUM' | 'DISBURSEMENTS' | 'ADVERSE_COSTS_OFFSET' | 'CRU' | 'INTERIM_PAYMENTS' | 'RETENTION'
export interface SettlementWordingState {
  explanations: Partial<Record<ExplanationKey, string>>
  agreement: string | null
  child: string | null
  // (B4 follow-up) The statement's own "Your rights" paragraph (null = default).
  rights: string | null
  defaults: { explanations: Record<ExplanationKey, string>; agreement: string; child: string; rights: string }
  rightsPlaceholders: { key: string; label: string }[]
  labels: { key: ExplanationKey; label: string }[]
  placeholders: { key: string; label: string }[]
  preview: {
    explanations: Record<ExplanationKey, string>
    agreement: string
    agreementSample: string
    child: string
    rights: string
    complaints: string
    sraFooter: string
  }
  previewHash: string
  problems: string[]
  approved: boolean
  approvedAt: string | null
  approvedBy: string | null
}
export interface SettlementWordingPatch {
  explanations?: Partial<Record<ExplanationKey, string | null>>
  agreement?: string | null
  child?: string | null
  rights?: string | null
}

// (B4 follow-up) Who acts for a child / protected party (PiMatter).
export type RepresentativeType = 'DEPUTY' | 'LPA_ATTORNEY' | 'OTHER'
export const REPRESENTATIVE_TYPE_LABELS: Record<RepresentativeType, string> = { DEPUTY: 'Deputy (Court of Protection)', LPA_ATTORNEY: 'Attorney (lasting power of attorney)', OTHER: 'Other' }
export interface Representatives {
  litigationFriendName: string
  litigationFriendRelationship: string
  // The OLD one-box address — read-only now (the original text; the server
  // keeps it in step when the separate fields below are saved).
  litigationFriendAddress: string
  // (B4 follow-ups) The address in the client's separate fields.
  litigationFriendAddressLine1: string
  litigationFriendAddressLine2: string
  litigationFriendAddressTown: string
  litigationFriendAddressCounty: string
  litigationFriendAddressPostcode: string
  litigationFriendEmail: string
  litigationFriendPhone: string
  // B4c (D4) — their UK mobile for e-signature codes: E.164 from the server
  // ('' = none); the card sends what was typed (the server normalises it).
  // (E-sign fixes B) null = "use the phone number" (it's a UK mobile).
  litigationFriendMobile?: string | null
  representativeType: RepresentativeType | null
  representativeName: string
  representativeReference: string
  representativeOrderDate: string | null
}
// (B4 follow-up 2) + the litigation friend's consent to receive bills /
// statements by email (Client details → Litigation friend).
export interface LitigationFriendState extends Representatives {
  // (B4 follow-ups) The fields were filled from the old one-box text (not yet
  // saved as fields) / the old text couldn't be split (shown as typed).
  litigationFriendAddressConverted?: boolean
  litigationFriendAddressLegacy?: boolean
  emailConsent: { at: string; email: string | null; method: string | null; recordedBy: string | null } | null
  // (Audit fix batch 2, Low 10) The server's rule: who bills / statements go to today
  // ('UNKNOWN' = can't tell), and whether the current statement is a child's / protected party's.
  capacityToday?: 'ADULT' | 'CHILD' | 'PROTECTED_PARTY' | 'UNKNOWN'
  statementForChildOrProtected?: boolean
}
// Who a bill / credit note / statement goes to (send windows).
export interface SendRecipient { kind: 'CLIENT' | 'LITIGATION_FRIEND'; name: string | null; relationship: string | null; missing: boolean; capacityUnknown?: boolean }

export const STATUS_LABEL: Record<StatementStatus, string> = { DRAFT: 'Draft', ISSUED: 'Issued', APPROVED: 'Approved by client', SUPERSEDED: 'Superseded' }
export const DESTINATION_LABEL: Record<NetDestination, string> = {
  CLIENT: 'To the client', COURT_FUNDS_OFFICE: 'Court Funds Office', CHILD_ACCOUNT: 'The child’s own account', DEPUTY: 'The deputy (Court of Protection)',
}
// (Batch 2) Where the NET SUM goes, as a label for a figure / column: "To the
// Court Funds Office £5,000.00". An LPA attorney is "To the attorney".
export const NET_SUM_LABEL: Record<NetDestination, string> = {
  CLIENT: 'To the client', COURT_FUNDS_OFFICE: 'To the Court Funds Office', CHILD_ACCOUNT: 'To the child’s own account', DEPUTY: 'To the deputy',
}
export const netSumLabel = (dest: NetDestination | null | undefined, representativeType?: RepresentativeType | null): string =>
  dest === 'DEPUTY' && representativeType === 'LPA_ATTORNEY' ? 'To the attorney' : NET_SUM_LABEL[dest ?? 'CLIENT']
export interface RegisterPage { rows: RegisterRow[]; nextCursor: string | null; limit: number }
export const DESTINATION_DETAIL_LABEL: Record<NetDestination, string> = {
  CLIENT: 'Payment details (optional)', COURT_FUNDS_OFFICE: 'Court Funds Office reference', CHILD_ACCOUNT: 'Account details', DEPUTY: 'Deputy (or attorney) name and order',
}
